import React, { useState, useRef, useEffect, useMemo } from "react";
import { IoChevronDown, IoChevronForward, IoChevronBack } from "react-icons/io5";
import { useTabs } from "contexts/TabContext";
import { useDealData } from "contexts/DealContext";
import { usePortfolioData } from "contexts/PortfolioContext";

const isTrailingPeriod = (p) => /^T(12|6|3|1)\b/.test(p);

const TimePeriodSelector = ({
    timePeriods = [],
    selectedPeriod,
    setTimePeriods,
    placeholder = "Select a time period",
    size = "medium",
    isPortfolioLoading = false,
    groupMonths = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [view, setView] = useState("trailing"); // 'trailing' | 'months' — only used when groupMonths
    const dropdownRef = useRef(null);
    const optionsRef = useRef([]);
    const [displayValue, setDisplayValue] = useState(placeholder);
    const { maintainingTab } = useTabs();
    const { isDealLoading } = useDealData();
    const { isPortfolioLoading: contextPortfolioLoading } = usePortfolioData();

    // Use portfolio loading if provided, otherwise use deal loading
    const isLoading = isPortfolioLoading !== false ? isPortfolioLoading : (contextPortfolioLoading || isDealLoading);

    // Check if the selector should be disabled during tab maintenance
    const isDisabled = maintainingTab && isLoading;

    // Update display value when selectedPeriod changes
    useEffect(() => {
        if (!selectedPeriod) {
            setDisplayValue(placeholder);
            return;
        }
        setDisplayValue(selectedPeriod);
    }, [selectedPeriod, placeholder]);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Split periods into trailing vs monthly (only used when groupMonths).
    const { trailingPeriods, monthlyPeriods } = useMemo(() => {
        const trailing = [];
        const monthly = [];
        for (const p of timePeriods) {
            if (isTrailingPeriod(p)) trailing.push(p);
            else monthly.push(p);
        }
        return { trailingPeriods: trailing, monthlyPeriods: monthly };
    }, [timePeriods]);

    // Build the current visible options list based on view + groupMonths flag.
    // Each option is { type: 'placeholder' | 'period' | 'select-month' | 'back', value?, label? }
    const currentOptions = useMemo(() => {
        if (!groupMonths) {
            return [
                { type: "placeholder", label: placeholder },
                ...timePeriods.map((p) => ({ type: "period", value: p, label: p })),
            ];
        }
        if (view === "months") {
            return [
                { type: "back", label: "Back" },
                ...monthlyPeriods.map((p) => ({ type: "period", value: p, label: p })),
            ];
        }
        // Trailing view
        return [
            { type: "placeholder", label: placeholder },
            ...trailingPeriods.map((p) => ({ type: "period", value: p, label: p })),
            ...(monthlyPeriods.length > 0
                ? [{ type: "select-month", label: "Select Month" }]
                : []),
        ];
    }, [groupMonths, view, timePeriods, trailingPeriods, monthlyPeriods, placeholder]);

    // Reset refs when option list changes
    useEffect(() => {
        optionsRef.current = optionsRef.current.slice(0, currentOptions.length);
    }, [currentOptions]);

    // When dropdown opens: if a month is selected, start on the months view;
    // otherwise start on the trailing view.
    useEffect(() => {
        if (!isOpen) return;
        if (groupMonths && selectedPeriod && !isTrailingPeriod(selectedPeriod)) {
            setView("months");
        } else {
            setView("trailing");
        }
        // Position active index on the selected period, or 0.
        const idx = selectedPeriod
            ? currentOptions.findIndex((o) => o.type === "period" && o.value === selectedPeriod)
            : 0;
        setActiveIndex(idx >= 0 ? idx : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Handler for selection
    const handleOptionAction = (option) => {
        if (isDisabled) return;
        if (option.type === "placeholder") {
            setIsOpen(false);
            setTimePeriods("");
        } else if (option.type === "period") {
            setIsOpen(false);
            setTimePeriods(option.value);
        } else if (option.type === "select-month") {
            setView("months");
            setActiveIndex(1); // skip the "Back" row
        } else if (option.type === "back") {
            setView("trailing");
            setActiveIndex(0);
        }
    };

    // Keyboard event handler
    const handleKeyDown = (e) => {
        if (isDisabled) return;

        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        const total = currentOptions.length;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex((i) => (i < total - 1 ? i + 1 : 0));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex((i) => (i > 0 ? i - 1 : total - 1));
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < total) {
                    handleOptionAction(currentOptions[activeIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;
            case 'Tab':
                setIsOpen(false);
                break;
            case 'Home':
                e.preventDefault();
                setActiveIndex(0);
                break;
            case 'End':
                e.preventDefault();
                setActiveIndex(total - 1);
                break;
            default: {
                const key = e.key.toLowerCase();
                if (key.length === 1 && /[a-z0-9]/.test(key)) {
                    const findFrom = (start) =>
                        currentOptions.findIndex(
                            (opt, idx) =>
                                idx > start &&
                                (opt.value || opt.label || "").toString().toLowerCase().startsWith(key)
                        );
                    const next = findFrom(activeIndex);
                    if (next !== -1) {
                        setActiveIndex(next);
                    } else {
                        const first = findFrom(-1);
                        if (first !== -1) setActiveIndex(first);
                    }
                }
                break;
            }
        }
    };

    // Scroll active option into view
    useEffect(() => {
        if (isOpen && activeIndex >= 0 && optionsRef.current[activeIndex]) {
            optionsRef.current[activeIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
    }, [activeIndex, isOpen]);

    const renderOption = (option, index) => {
        const isActive = activeIndex === index;
        const baseClasses = `px-4 py-2 font-medium cursor-pointer ${isActive ? "bg-blue-100" : "hover:bg-gray-100"}`;

        if (option.type === "placeholder") {
            return (
                <li
                    key="placeholder"
                    ref={(el) => (optionsRef.current[index] = el)}
                    className={`${baseClasses} text-gray-500`}
                    onClick={() => handleOptionAction(option)}
                    onMouseEnter={() => setActiveIndex(index)}
                    role="option"
                    aria-selected={isActive}
                    tabIndex="-1"
                >
                    {option.label}
                </li>
            );
        }
        if (option.type === "select-month") {
            return (
                <li
                    key="select-month"
                    ref={(el) => (optionsRef.current[index] = el)}
                    className={`${baseClasses} flex items-center justify-between border-t border-gray-200 text-gray-700`}
                    onClick={() => handleOptionAction(option)}
                    onMouseEnter={() => setActiveIndex(index)}
                    role="option"
                    aria-selected={isActive}
                    tabIndex="-1"
                >
                    <span>{option.label}</span>
                    <IoChevronForward className="w-3 h-3" />
                </li>
            );
        }
        if (option.type === "back") {
            return (
                <li
                    key="back"
                    ref={(el) => (optionsRef.current[index] = el)}
                    className={`${baseClasses} flex items-center gap-2 border-b border-gray-200 text-gray-700`}
                    onClick={() => handleOptionAction(option)}
                    onMouseEnter={() => setActiveIndex(index)}
                    role="option"
                    aria-selected={isActive}
                    tabIndex="-1"
                >
                    <IoChevronBack className="w-3 h-3" />
                    <span>{option.label}</span>
                </li>
            );
        }
        // period
        return (
            <li
                key={option.value}
                ref={(el) => (optionsRef.current[index] = el)}
                className={baseClasses}
                onClick={() => handleOptionAction(option)}
                onMouseEnter={() => setActiveIndex(index)}
                role="option"
                aria-selected={isActive}
                tabIndex="-1"
            >
                {option.label}
            </li>
        );
    };

    return (
        <div
            className={`relative lg:min-w-[200px] xl:min-w-[250px] ${isDisabled ? 'opacity-50 pointer-events-none' : ''}`}
            ref={dropdownRef}
            onKeyDown={handleKeyDown}
            tabIndex={isDisabled ? '-1' : '0'}
        >
            <div
                className={`flex items-center justify-between px-3 py-2 bg-white border-b border-[#89949f] cursor-pointer ${size === "small" ? "text-sm" : "text-base"} ${isOpen ? "border-blue-500" : ""}`}
                onClick={() => {
                    if (!isDisabled && !isLoading) {
                        setIsOpen(!isOpen);
                    }
                }}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls="time-period-selector"
                role="combobox"
            >
                <span className="truncate overflow-hidden">{isLoading ? "Loading..." : displayValue}</span>
                {!isDisabled && (
                    <IoChevronDown className={`w-[12px] h-[12px] transition-transform ml-2 ${isOpen ? 'rotate-180' : ''}`} />
                )}
            </div>

            {isOpen && !isDisabled && !isLoading && (
                <div
                    className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto w-full"
                    role="listbox"
                >
                    <ul className="py-1">
                        {currentOptions.map((option, index) => renderOption(option, index))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default TimePeriodSelector;
